import React, { useState } from "react";
import {
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Skeleton,
  Tabs,
  Tab,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CreateTutorialJourneyCommand,
  TutorialJourneyType,
  ITutorialJourneyProfileDto,
  ITutorialJourneyActivatorDto,
  ITutorialJourneyDto,
  TutorialJourneyActivatorDto,
  CreateTutorialJourneyProfileCommand,
  UpdateTutorialJourneyProfileCommand,
} from "@growth-machine-llc/stridist-api-client";
import TutorialJourneysService from "../../../services/TutorialJourneysService";
import makeStyles from "@mui/styles/makeStyles";
import { ADMIN_JOURNEY_TEMPLATES_ROUTE } from "../../../routes/routes";
import { ConfirmActionDialog } from "../../dialog/ConfirmActionDialog";
import { JourneyTableRow } from "./JourneyTableRow";
import TutorialJourneyProfilesService from "../../../services/TutorialJourneyProfilesService";
import { ProfileTableRow } from "./JourneyProfileTableRow";
import { useDeleteTutorialJourney } from "../hooks/useDeleteTutorialJourney";
import { useCreateTutorialJourney } from "../hooks/useCreateTutorialJourney";
import { useDeleteTutorialJourneyProfile } from "../hooks/useDeleteTutorialJourneyProfile";
import { useUpdateTutorialJourneyProfile } from "../hooks/useUpdateTutorialJourneyProfile";
import { ProfileUsageDialog } from "./JourneyProfileUsageDialog";
import TutorialJourneyActivatorsService from "../../../services/TutorialJourneyActivatorsService";
import { useCreateTutorialJourneyActivator } from "../hooks/useCreateTutorialJourneyActivator";
import { JourneyActivatorRow } from "./JourneyActivatorsRow";
import { useCreateTutorialJourneyProfile } from "../hooks/useCreateTutorialJourneyProfile";
import { JourneyProfileDialog } from "../dialogs/JourneyProfileDialog";
import { JourneyDialog } from "../dialogs/JourneyDialog";
import { JourneyActivatorDialog } from "../dialogs/JourneyActivatorDialog";
import { useDeleteJourneyActivator } from "../hooks/useDeleteJourneyActivator";
import { TutorialType } from "../../../constants";
import { ProfilePayload } from "../dialogs/JourneyProfileDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.spacing(1.5),
    padding: 1,
    boxShadow: theme.shadows[4],
    marginBottom: theme.spacing(3),
    maxWidth: "1200px",
  },

  container: {
    margin: theme.spacing(3, 2),
    [theme.breakpoints.up("md")]: {
      margin: theme.spacing(3, 6),
    },
  },

  table: {
    minWidth: 650,
    "& th": {
      fontWeight: "bold",
      fontSize: 15,
    },
    "& td": {
      fontWeight: 500,
      fontSize: 14,
    },
    "& tfoot td": {
      borderBottom: "none",
    },
  },

  box: {
    display: "flex",
    justifyContent: "flex-start",
  },

  addButton: {
    marginTop: theme.spacing(2) + " !important",
    marginLeft: theme.spacing(2) + " !important",
  },

  dialogPaper: {
    width: "450px",
    maxWidth: "90%",
  },

  fullWidthButton: {
    width: "100%",
    justifyContent: "center",
    padding: theme.spacing(1.5),
    fontSize: 16,
  },
}));

export const TUTORIAL_JOURNEYS_QUERY_KEY = "tutorial-journeys";
export const TUTORIAL_PROFILES_QUERY_KEY = "tutorial-journey-profiles";
export const TUTORIAL_ACTIVATORS_QUERY_KEY = "tutorial-activators";

enum TutorialJourneyTab {
  JOURNEYS = "JOURNEYS",
  JOURNEY_ACTIVATORS = "JOURNEY ACTIVATORS",
  PROFILES = "PROFILES",
}

type DeletableItem =
  | { type: TutorialType.Journey; data: ITutorialJourneyDto }
  | { type: TutorialType.Profile; data: ITutorialJourneyProfileDto }
  | { type: TutorialType.Activator; data: ITutorialJourneyActivatorDto };

function useDeleteTutorialItem() {
  const { mutate: deleteJourney, isPending: journeyPending } =
    useDeleteTutorialJourney();
  const { mutate: deleteProfile, isPending: profilePending } =
    useDeleteTutorialJourneyProfile();
  const { mutate: deleteActivator, isPending: activatorPending } =
    useDeleteJourneyActivator();

  const deleteItem = (
    id: number,
    type: "journey" | "profile" | "activator",
  ) => {
    if (type === "journey") {
      deleteJourney(id);
    } else if (type === "profile") {
      deleteProfile(id);
    } else if (type === "activator") {
      deleteActivator(id);
    }
  };

  return {
    deleteItem,
    isPending: journeyPending || profilePending || activatorPending,
  };
}

export function TutorialJourneysList() {
  const s = useStyles();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTabParam = searchParams.get("tab");
  const initialTab =
    initialTabParam === "activators"
      ? TutorialJourneyTab.JOURNEY_ACTIVATORS
      : initialTabParam === "profiles"
        ? TutorialJourneyTab.PROFILES
        : TutorialJourneyTab.JOURNEYS;
  const [selectedTab, setSelectedTab] =
    useState<TutorialJourneyTab>(initialTab);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [tutorialTitle, setTutorialTitle] = useState("");
  const [newJourneyType, setNewJourneyType] = useState<"" | number>("");

  const [itemToDelete, setItemToDelete] = useState<DeletableItem | null>(null);

  const [profileUsageDialogOpen, setProfileUsageDialogOpen] = useState(false);
  const [selectedProfileForUsage, setSelectedProfileForUsage] =
    useState<ITutorialJourneyProfileDto | null>(null);

  const [activatorDialogOpen, setActivatorDialogOpen] = useState(false);
  const [selectedJourneyType, setSelectedJourneyType] = useState<
    TutorialJourneyType | ""
  >("");

  const [editorDialogOpen, setEditorDialogOpen] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [profileAvatar, setProfileAvatar] = useState("");

  const { data: tutorialJourneys, isLoading: journeysLoading } = useQuery({
    queryKey: [TUTORIAL_JOURNEYS_QUERY_KEY],
    queryFn: () => TutorialJourneysService.getTutorialJourneys(),
    enabled:
      selectedTab === TutorialJourneyTab.JOURNEYS ||
      selectedTab === TutorialJourneyTab.JOURNEY_ACTIVATORS,
  });

  const { data: profilesVm, isLoading: profilesLoading } = useQuery({
    queryKey: [TUTORIAL_PROFILES_QUERY_KEY],
    queryFn: () => TutorialJourneyProfilesService.getTutorialJourneysProfiles(),
    enabled: selectedTab === TutorialJourneyTab.PROFILES,
  });

  const { data: activatorsVm, isLoading: activatorsLoading } = useQuery({
    queryKey: [TUTORIAL_ACTIVATORS_QUERY_KEY],
    queryFn: () =>
      TutorialJourneyActivatorsService.getTutorialJourneysActivators(),
    enabled: selectedTab === TutorialJourneyTab.JOURNEY_ACTIVATORS,
  });

  const { mutate: createTutorialJourney, isPending: creatingTutorialJourney } =
    useCreateTutorialJourney();
  const { deleteItem, isPending: deletingItem } = useDeleteTutorialItem();
  const isPending = creatingTutorialJourney || deletingItem;

  const {
    mutate: createActivatorMutation,
    isPending: creatingActivatorMutation,
  } = useCreateTutorialJourneyActivator();

  const { mutate: createProfile, isPending: creatingProfile } =
    useCreateTutorialJourneyProfile();

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: TutorialJourneyTab,
  ) => {
    setSelectedTab(newValue);
    const tabParam =
      newValue === TutorialJourneyTab.JOURNEY_ACTIVATORS
        ? "activators"
        : newValue === TutorialJourneyTab.PROFILES
          ? "profiles"
          : "journeys";
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tabParam);
      return next;
    });
  };

  const handleAddClick = () => {
    setTutorialTitle("");
    setNewJourneyType("");
    setDialogOpen(true);
  };

  const handleForwardClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    journey: ITutorialJourneyDto,
  ) => {
    e.stopPropagation();
    navigate(
      `${ADMIN_JOURNEY_TEMPLATES_ROUTE}/journey/${TutorialJourneyType[journey.type]}`,
    );
  };

  const handleActivatorForwardClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    activator: ITutorialJourneyActivatorDto,
  ) => {
    e.stopPropagation();
    navigate(`${ADMIN_JOURNEY_TEMPLATES_ROUTE}/activator/${activator.id}`);
  };

  const handleProfileForwardClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    profile: ITutorialJourneyProfileDto,
  ) => {
    e.stopPropagation();
    setSelectedProfileForUsage(profile);
    setProfileUsageDialogOpen(true);
  };

  const handleProfileDeleteClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    profile: ITutorialJourneyProfileDto,
  ) => {
    e.stopPropagation();
    setItemToDelete({ type: TutorialType.Profile, data: profile });
  };

  const handleActivatorDeleteClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    activator: ITutorialJourneyActivatorDto,
  ) => {
    e.stopPropagation();
    setItemToDelete({ type: TutorialType.Activator, data: activator });
  };

  const handleDeleteTutorialClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    journey: ITutorialJourneyDto,
  ) => {
    e.stopPropagation();
    setItemToDelete({ type: TutorialType.Journey, data: journey });
  };

  const confirmDelete = () => {
    if (!itemToDelete) return;
    const { type, data } = itemToDelete;
    deleteItem(data.id, type);
    setItemToDelete(null);
  };

  const cancelDelete = () => {
    setItemToDelete(null);
  };

  const handleAddActivatorClick = () => {
    setSelectedJourneyType("");
    setActivatorDialogOpen(true);
  };

  const handleAddProfileClick = () => {
    setProfileName("");
    setProfileAvatar("");
    setEditorDialogOpen(true);
  };

  const { mutate: updateProfile, isPending: updatingProfile } =
    useUpdateTutorialJourneyProfile();

  const [editingProfile, setEditingProfile] =
    useState<ITutorialJourneyProfileDto | null>(null);

  const handleEditProfileClick = (profile: ITutorialJourneyProfileDto) => {
    setEditingProfile(profile);
    setEditorDialogOpen(true);
  };

  const getItemLabel = (item: DeletableItem): string => {
    switch (item.type) {
      case "journey":
        return (item.data as ITutorialJourneyDto).title;
      case "profile":
        return (item.data as ITutorialJourneyProfileDto).name;
      case "activator": {
        const activator = item.data as ITutorialJourneyActivatorDto;
        if (activator.tutorialJourneyId) {
          return `Activator for Journey ID ${activator.tutorialJourneyId}`;
        }
        return `Activator #${activator.id || ""}`;
      }
      default:
        return "";
    }
  };

  const availableJourneys =
    profilesVm && profilesVm.journeys
      ? profilesVm.journeys
      : tutorialJourneys || [];

  const filteredJourneys =
    tutorialJourneys?.filter(
      (journey) =>
        !activatorsVm?.activators?.some(
          (activator) => activator.tutorialJourneyId === journey.id,
        ),
    ) || [];

  return (
    <Paper className={s.root}>
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="JOURNEYS" value={TutorialJourneyTab.JOURNEYS} />
        <Tab
          label="JOURNEY ACTIVATORS"
          value={TutorialJourneyTab.JOURNEY_ACTIVATORS}
        />
        <Tab label="PROFILES" value={TutorialJourneyTab.PROFILES} />
      </Tabs>

      <Box className={s.box}>
        {selectedTab === TutorialJourneyTab.JOURNEYS && (
          <Button
            className={s.addButton}
            variant="contained"
            onClick={handleAddClick}
            disabled={isPending}
          >
            Add New
          </Button>
        )}
        {selectedTab === TutorialJourneyTab.JOURNEY_ACTIVATORS && (
          <Button
            className={s.addButton}
            variant="contained"
            onClick={handleAddActivatorClick}
            disabled={creatingActivatorMutation}
          >
            Add New
          </Button>
        )}
        {selectedTab === TutorialJourneyTab.PROFILES && (
          <Button
            className={s.addButton}
            variant="contained"
            onClick={handleAddProfileClick}
          >
            Add New
          </Button>
        )}
      </Box>

      {selectedTab === TutorialJourneyTab.JOURNEYS && (
        <Box className={s.container}>
          <Table className={s.table} aria-label="tutorial journeys table">
            {journeysLoading ? (
              <TableBody>
                {[...Array(3)].map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {Array.from({ length: 7 }).map((_, cellIndex) => (
                      <TableCell key={cellIndex} align="center">
                        <Skeleton variant="rectangular" height={30} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="center">Steps</TableCell>
                    <TableCell align="center">Author</TableCell>
                    <TableCell align="center">Updates</TableCell>
                    <TableCell align="center">Modified By</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tutorialJourneys && tutorialJourneys.length ? (
                    tutorialJourneys.map((journey: ITutorialJourneyDto) => (
                      <JourneyTableRow
                        key={journey.id}
                        journey={journey}
                        onForwardClick={handleForwardClick}
                        onDeleteClick={handleDeleteTutorialClick}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No tutorial journey templates found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </>
            )}
          </Table>
        </Box>
      )}

      {selectedTab === TutorialJourneyTab.JOURNEY_ACTIVATORS && (
        <Box className={s.container}>
          <Table className={s.table} aria-label="journey activators table">
            {activatorsLoading ? (
              <TableBody>
                {[...Array(3)].map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {Array.from({ length: 7 }).map((_, cellIndex) => (
                      <TableCell key={cellIndex} align="center">
                        <Skeleton variant="rectangular" height={30} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Journey Name</TableCell>
                    <TableCell align="center">Journey Type</TableCell>
                    <TableCell align="center">Author</TableCell>
                    <TableCell align="center">Updates</TableCell>
                    <TableCell align="center">Modified By</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activatorsVm?.activators?.length ? (
                    activatorsVm.activators.map((activator) => (
                      <JourneyActivatorRow
                        key={activator.id}
                        activator={activator}
                        journeys={tutorialJourneys || []}
                        onForwardClick={handleActivatorForwardClick}
                        onDeleteClick={handleActivatorDeleteClick}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No tutorial journey activators found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </>
            )}
          </Table>
        </Box>
      )}

      {selectedTab === TutorialJourneyTab.PROFILES && (
        <Box className={s.container}>
          <Table
            className={s.table}
            aria-label="tutorial journey profiles table"
          >
            {profilesLoading ? (
              <TableBody>
                {[...Array(3)].map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {Array.from({ length: 7 }).map((_, cellIndex) => (
                      <TableCell key={cellIndex} align="center">
                        <Skeleton variant="rectangular" height={30} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <>
                <TableHead>
                  <TableRow>
                    <TableCell>Avatar</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="center">Description</TableCell>
                    <TableCell align="center">Author</TableCell>
                    <TableCell align="center">Updates</TableCell>
                    <TableCell align="center">Modified By</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {profilesVm &&
                  profilesVm.profiles &&
                  profilesVm.profiles.length ? (
                    profilesVm.profiles.map(
                      (profile: ITutorialJourneyProfileDto) => (
                        <ProfileTableRow
                          key={profile.id}
                          profile={profile}
                          onForwardClick={handleProfileForwardClick}
                          onDeleteClick={handleProfileDeleteClick}
                          onEditClick={() => handleEditProfileClick(profile)}
                        />
                      ),
                    )
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No tutorial journey profiles found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </>
            )}
          </Table>
        </Box>
      )}

      <JourneyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={(title, type, description) => {
          const payload = CreateTutorialJourneyCommand.fromJS({
            title,
            type,
            description,
          });
          createTutorialJourney(payload);
        }}
        isPending={creatingTutorialJourney}
        tutorialJourneys={tutorialJourneys}
      />

      <JourneyActivatorDialog
        open={activatorDialogOpen}
        onClose={() => setActivatorDialogOpen(false)}
        onSave={(type, description) =>
          createActivatorMutation({ type, description })
        }
        journeys={filteredJourneys}
        isPending={creatingActivatorMutation}
      />

      <JourneyProfileDialog
        open={editorDialogOpen}
        onClose={() => {
          setEditorDialogOpen(false);
          setEditingProfile(null);
        }}
        onSave={(p: ProfilePayload) => {
          const basePayload = {
            name: p.name,
            subtitle: p.subtitle,
            avatar: p.avatar,
            description: p.description,
          };

          if (p.id) {
            updateProfile(
              UpdateTutorialJourneyProfileCommand.fromJS({
                id: p.id,
                ...basePayload,
              }),
            );
          } else {
            createProfile(
              CreateTutorialJourneyProfileCommand.fromJS(basePayload),
            );
          }
        }}
        isPending={updatingProfile || creatingProfile}
        initialData={
          editingProfile
            ? {
                id: editingProfile.id,
                name: editingProfile.name,
                avatar: editingProfile.avatar || "",
                subtitle: editingProfile.subtitle,
                description: editingProfile.description,
              }
            : undefined
        }
      />

      <ConfirmActionDialog
        open={Boolean(itemToDelete)}
        title={`Are you sure you want to delete "${
          itemToDelete ? getItemLabel(itemToDelete) : ""
        }"?`}
        description="This action cannot be undone."
        onClose={cancelDelete}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
      />

      {selectedProfileForUsage && (
        <ProfileUsageDialog
          open={profileUsageDialogOpen}
          onClose={() => setProfileUsageDialogOpen(false)}
          profile={selectedProfileForUsage}
          journeys={availableJourneys}
        />
      )}
    </Paper>
  );
}
